/**
 * รถม้า: the station coach and its horse, and the station's signpost with its hitching rail
 * (carriage rules: packages/shared/src/carriage.ts), drawn in code in the realistic pixel art style.
 *
 * Everything is modelled in 3D, in world pixels (a tile is 32/√2 of them on a side; up is up): the
 * coach along `f` (toward the horse), `l` (to its right) and `u`. Each part is sampled as a surface
 * (ellipsoids, tubes, boxes, rings), projected with the game's own 2:1 projection for each of the
 * five drawn headings (the other three are mirrored, as for the figures), kept nearest-first in a
 * depth buffer, and lit by the warm key light from the upper right: every material shaded in a few
 * hard tones of its own (cool shadow, warm light, a sheen on lacquer, leather, metal and horse hair).
 *
 * The coach (its wheels turned through `FRAMES` frames of one spoke's turn) and the horse (idle,
 * walk and trot, each a continuous phase in `FRAMES` frames) are made separately, depth and all,
 * and put together per drawn frame, so the shafts pass properly in front of and behind the horse.
 * The wheels and the gait are paced by the distance the coach has rolled, so neither slides.
 */
import { CARRIAGE } from '@nine/shared';
import { canDraw, timed } from './drawBudget';
import { canvas, ctx2d, hexRgb, outline } from './pixel';
import { FRAMES, type View } from './sprites';

/** World pixels to a tile's side. */
const TILE = 32 / Math.SQRT2;
/**
 * The model is measured in true world pixels; it is drawn this much larger, to stand with the
 * game's figures (drawn taller than life against the tiles), as data/carriage.ts places it.
 */
const SCALE = 1.5;
/** Surface samples per drawn pixel along each way, so no gaps open between them. */
const DENS = 2.2 * SCALE;
/** The frame every carriage picture is drawn in, and where the coach's middle stands on the ground. */
export const CARRIAGE_W = 198;
export const CARRIAGE_H = 196;
export const CARRIAGE_AX = 66;
export const CARRIAGE_AY = 138;

/** Ground angle of each drawn view's forward direction, clockwise from screen-right (degrees). */
const VIEW_ANGLE: Record<View, number> = { side: 0, front3: 45, front: 90, back3: -45, back: -90 };
/** Drawn view and mirroring for each screen heading (0 = right, clockwise), as the figures. */
const HEADING_VIEW: readonly (readonly [View, 1 | -1])[] = [
  ['side', 1],
  ['front3', 1],
  ['front', 1],
  ['front3', -1],
  ['side', -1],
  ['back3', -1],
  ['back', 1],
  ['back3', 1],
];
export const carriageView = (heading: number): readonly [View, 1 | -1] =>
  HEADING_VIEW[((heading % 8) + 8) % 8]!;

// ───────────────────────── materials ─────────────────────────

type RGB = readonly [number, number, number];

/** A surface: its colour, how glossy it is, and whether its shadow runs warm (skin) or cool. */
interface Mat {
  ramp: readonly number[];
  /** Sheen strength (0 matte) and tightness. */
  spec: number;
  pow: number;
}

const pack = ([r, g, b]: RGB): number =>
  (255 << 24) |
  (Math.max(0, Math.min(255, Math.round(b))) << 16) |
  (Math.max(0, Math.min(255, Math.round(g))) << 8) |
  Math.max(0, Math.min(255, Math.round(r)));
const mix = (a: RGB, b: RGB, k: number): RGB => [
  a[0] + (b[0] - a[0]) * k,
  a[1] + (b[1] - a[1]) * k,
  a[2] + (b[2] - a[2]) * k,
];
const mul = (a: RGB, k: number): RGB => [a[0] * k, a[1] * k, a[2] * k];

const COOL: RGB = [38, 50, 92];
const WARM_SHADOW: RGB = [120, 40, 38];
const KEY: RGB = [255, 236, 196];

/** Five hard tones from deep shadow to the sheen, and the sheen's colour. */
function mat(hex: string, spec = 0, pow = 12, warmShadow = false): Mat {
  const c = hexRgb(hex) as RGB;
  const shadow = warmShadow ? WARM_SHADOW : COOL;
  return {
    ramp: [
      pack(mix(mul(c, 0.42), shadow, 0.3)),
      pack(mix(mul(c, 0.66), shadow, 0.18)),
      pack(c),
      pack(mix(mul(c, 1.16), KEY, 0.14)),
      pack(mix(mul(c, 1.3), KEY, 0.5)),
    ],
    spec,
    pow,
  };
}

const M = {
  lacquer: mat('#6e2e22', 0.55, 16),
  lacquerDark: mat('#40201a', 0.45, 14),
  frame: mat('#3c2a1e', 0.2, 10),
  wood: mat('#86603c', 0.08, 8),
  woodDark: mat('#5a3e26', 0.06, 8),
  iron: mat('#5c6068', 0.9, 26),
  bronze: mat('#9a7038', 0.85, 18),
  roof: mat('#39403e', 0.3, 9),
  roofRib: mat('#262b2a', 0.2, 9),
  curtain: mat('#34405e', 0, 8),
  curtainFold: mat('#283250', 0, 8),
  blind: mat('#b09464', 0.1, 8),
  blindSlat: mat('#7e6640', 0.05, 8),
  interior: mat('#1c1416', 0, 8),
  coat: mat('#7e4426', 0.32, 11),
  coatDark: mat('#5a2e1c', 0.3, 11),
  points: mat('#2a1d18', 0.5, 15),
  hoof: mat('#3e3630', 0.35, 14),
  feather: mat('#d4cab8', 0.15, 8),
  blaze: mat('#e2d8c6', 0.15, 10),
  muzzle: mat('#4a3328', 0.3, 12),
  eye: mat('#100c0c', 1, 30),
  leather: mat('#4a2a1a', 0.65, 20),
  skin: mat('#d29e78', 0.12, 10, true),
  hair: mat('#1e1816', 0.5, 16),
  tunic: mat('#55604e', 0, 8),
  tunicDark: mat('#3e4638', 0, 8),
  straw: mat('#b69858', 0.12, 8),
  sash: mat('#7a3a28', 0.1, 8),
  board: mat('#5c2a1e', 0.5, 14),
  paint: mat('#d8c890', 0.05, 8),
  stone: mat('#77736a', 0.05, 8),
} as const;

// Key light from the upper right (and a little toward the viewer); the eye looks down the 2:1 slope.
const LIGHT = (() => {
  const v = [0.62, 0.3, 0.72];
  const l = Math.hypot(...v);
  return v.map((x) => x / l) as [number, number, number];
})();
const EYE = [0, 0.894, 0.447] as const;
const HALF = (() => {
  const v = LIGHT.map((x, i) => x + EYE[i]!);
  const l = Math.hypot(...v);
  return v.map((x) => x / l) as [number, number, number];
})();

// ───────────────────────── the rasteriser ─────────────────────────

/** One layer drawn for one view: colours and nearness per pixel, and the ground under it. */
interface Layer {
  col: Uint32Array;
  near: Float32Array;
  shadow: Uint8Array;
}

type P3 = readonly [number, number, number];

class Raster implements Layer {
  readonly col = new Uint32Array(CARRIAGE_W * CARRIAGE_H);
  readonly near = new Float32Array(CARRIAGE_W * CARRIAGE_H).fill(-1e9);
  readonly shadow = new Uint8Array(CARRIAGE_W * CARRIAGE_H);
  private readonly c: number;
  private readonly s: number;

  constructor(view: View) {
    const a = (VIEW_ANGLE[view] * Math.PI) / 180;
    this.c = Math.cos(a);
    this.s = Math.sin(a);
  }

  /** A surface point (f, l, u) with its outward normal, shaded and kept if nearest. */
  put(f: number, l: number, u: number, nf: number, nl: number, nu: number, m: Mat, tone = 0): void {
    const { c, s } = this;
    f *= SCALE;
    l *= SCALE;
    u *= SCALE;
    const r = f * c - l * s;
    const t = f * s + l * c;
    const nr = nf * c - nl * s;
    const nt = nf * s + nl * c;
    if (nt * EYE[1] + nu * EYE[2] < -0.12) return;
    const x = Math.floor(CARRIAGE_AX + r + 0.5);
    const yg = CARRIAGE_AY + 0.5 * t;
    const y = Math.floor(yg - u + 0.5);
    if (x < 0 || y < 0 || x >= CARRIAGE_W || y >= CARRIAGE_H) return;
    const i = y * CARRIAGE_W + x;
    // The ground under it, for the shadow beneath the coach.
    const gy = Math.floor(yg + 0.5);
    if (gy >= 0 && gy < CARRIAGE_H) this.shadow[gy * CARRIAGE_W + x] = 1;
    const near = t + 0.5 * u;
    if (near <= this.near[i]!) return;
    this.near[i] = near;
    const diff = Math.max(0, nr * LIGHT[0] + nt * LIGHT[1] + nu * LIGHT[2]);
    let v = 0.3 + 0.8 * diff + tone;
    const h = Math.max(0, nr * HALF[0] + nt * HALF[1] + nu * HALF[2]);
    if (m.spec > 0 && h ** m.pow * m.spec > 0.42) v = 2;
    const k = v < 0.46 ? 0 : v < 0.68 ? 1 : v < 0.95 ? 2 : v < 1.6 ? 3 : 4;
    this.col[i] = m.ramp[k]!;
  }

  /** An ellipsoid at `p` with radii (a along f, b along l, c up). */
  ellipsoid(p: P3, a: number, b: number, cr: number, m: Mat, tone = 0): void {
    const n = Math.max(12, Math.ceil(Math.max(a, b, cr) * 2 * Math.PI * 1.8 * SCALE));
    const rows = Math.ceil(n / 2);
    for (let j = 0; j <= rows; j++) {
      const ph = -Math.PI / 2 + (Math.PI * j) / rows;
      const cp = Math.cos(ph);
      const sp = Math.sin(ph);
      const ring = Math.max(6, Math.ceil(n * cp));
      for (let k = 0; k < ring; k++) {
        const th = (2 * Math.PI * k) / ring;
        const x = cp * Math.cos(th);
        const y = cp * Math.sin(th);
        const nf = x / a;
        const nl = y / b;
        const nu = sp / cr;
        const nn = Math.hypot(nf, nl, nu) || 1;
        this.put(p[0] + a * x, p[1] + b * y, p[2] + cr * sp, nf / nn, nl / nn, nu / nn, m, tone);
      }
    }
  }

  /** A tube from `p0` (radius r0) to `p1` (radius r1), its ends rounded when `caps`. */
  tube(p0: P3, p1: P3, r0: number, r1: number, m: Mat, caps = true, tone = 0): void {
    const d = [p1[0] - p0[0], p1[1] - p0[1], p1[2] - p0[2]] as const;
    const len = Math.hypot(...d);
    if (len < 1e-6) return this.ellipsoid(p0, r0, r0, r0, m, tone);
    const a = d.map((x) => x / len) as [number, number, number];
    // Two directions across the tube.
    const ref = Math.abs(a[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
    let e1 = [
      a[1] * ref[2]! - a[2] * ref[1]!,
      a[2] * ref[0]! - a[0] * ref[2]!,
      a[0] * ref[1]! - a[1] * ref[0]!,
    ];
    const l1 = Math.hypot(...e1);
    e1 = e1.map((x) => x / l1);
    const e2 = [a[1] * e1[2]! - a[2] * e1[1]!, a[2] * e1[0]! - a[0] * e1[2]!, a[0] * e1[1]! - a[1] * e1[0]!];
    const steps = Math.ceil(len * DENS);
    const ring = Math.max(8, Math.ceil(Math.max(r0, r1) * 2 * Math.PI * DENS));
    for (let i = 0; i <= steps; i++) {
      const s = i / steps;
      const r = r0 + (r1 - r0) * s;
      for (let k = 0; k < ring; k++) {
        const th = (2 * Math.PI * k) / ring;
        const nx = Math.cos(th) * e1[0]! + Math.sin(th) * e2[0]!;
        const ny = Math.cos(th) * e1[1]! + Math.sin(th) * e2[1]!;
        const nz = Math.cos(th) * e1[2]! + Math.sin(th) * e2[2]!;
        this.put(
          p0[0] + d[0] * s + nx * r,
          p0[1] + d[1] * s + ny * r,
          p0[2] + d[2] * s + nz * r,
          nx,
          ny,
          nz,
          m,
          tone,
        );
      }
    }
    if (caps) {
      this.ellipsoid(p0, r0, r0, r0, m, tone);
      this.ellipsoid(p1, r1, r1, r1, m, tone);
    }
  }

  /** A chain of tubes through the points, tapering from r0 to r1. */
  chain(pts: readonly P3[], r0: number, r1: number, m: Mat, tone = 0): void {
    const n = pts.length - 1;
    for (let i = 0; i < n; i++)
      this.tube(
        pts[i]!,
        pts[i + 1]!,
        r0 + ((r1 - r0) * i) / n,
        r0 + ((r1 - r0) * (i + 1)) / n,
        m,
        true,
        tone,
      );
  }

  /**
   * A flat quad from `o` along `a` and `b` (any lengths), facing `n`; `paint(s, t)` (0–1 along a
   * and b) may give another material, a tone, or `null` for a hole.
   */
  quad(o: P3, a: P3, b: P3, n: P3, m: Mat, paint?: (s: number, t: number) => Mat | number | null): void {
    const la = Math.hypot(...a);
    const lb = Math.hypot(...b);
    const na = Math.ceil(la * DENS);
    const nb = Math.ceil(lb * DENS);
    for (let i = 0; i <= na; i++)
      for (let j = 0; j <= nb; j++) {
        const s = i / na;
        const t = j / nb;
        const p = paint?.(s, t);
        if (p === null) continue;
        const mm = typeof p === 'object' ? p : m;
        const tone = typeof p === 'number' ? p : 0;
        this.put(
          o[0] + a[0] * s + b[0] * t,
          o[1] + a[1] * s + b[1] * t,
          o[2] + a[2] * s + b[2] * t,
          n[0],
          n[1],
          n[2],
          mm,
          tone,
        );
      }
  }

  /** A box [f0, f1] × [l0, l1] × [u0, u1]; `paint` gets the face ('f+', 'l-', 'u+'…) and (s, t). */
  box(
    f0: number,
    f1: number,
    l0: number,
    l1: number,
    u0: number,
    u1: number,
    m: Mat,
    paint?: (face: string, s: number, t: number) => Mat | number | null,
  ): void {
    const F = f1 - f0;
    const L = l1 - l0;
    const U = u1 - u0;
    const p = (face: string) => (paint ? (s: number, t: number) => paint(face, s, t) : undefined);
    // Sides: s runs along the face, t up it.
    this.quad([f0, l1, u0], [F, 0, 0], [0, 0, U], [0, 1, 0], m, p('l+'));
    this.quad([f1, l0, u0], [-F, 0, 0], [0, 0, U], [0, -1, 0], m, p('l-'));
    this.quad([f1, l1, u0], [0, -L, 0], [0, 0, U], [1, 0, 0], m, p('f+'));
    this.quad([f0, l0, u0], [0, L, 0], [0, 0, U], [-1, 0, 0], m, p('f-'));
    this.quad([f0, l0, u1], [F, 0, 0], [0, L, 0], [0, 0, 1], m, p('u+'));
  }

  /** A ring (a torus) round `c` in the plane of unit `a` and `b`, radius R, tube radius r, over [t0, t1). */
  ring(c: P3, a: P3, b: P3, R: number, r: number, m: Mat, t0 = 0, t1 = 2 * Math.PI): void {
    const n = [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
    const steps = Math.ceil(R * (t1 - t0) * DENS);
    const ring = Math.max(6, Math.ceil(r * 2 * Math.PI * DENS));
    for (let i = 0; i <= steps; i++) {
      const th = t0 + ((t1 - t0) * i) / steps;
      const rad = [0, 1, 2].map((k) => Math.cos(th) * a[k]! + Math.sin(th) * b[k]!);
      for (let k = 0; k < ring; k++) {
        const be = (2 * Math.PI * k) / ring;
        const nn = [0, 1, 2].map((q) => Math.cos(be) * rad[q]! + Math.sin(be) * n[q]!);
        this.put(
          c[0] + rad[0]! * R + nn[0]! * r,
          c[1] + rad[1]! * R + nn[1]! * r,
          c[2] + rad[2]! * R + nn[2]! * r,
          nn[0]!,
          nn[1]!,
          nn[2]!,
          m,
        );
      }
    }
  }
}

// ───────────────────────── the coach ─────────────────────────

/** The coach's measures (world pixels): wheels, cabin, roof, bench and shafts. */
const WHEEL = { f: -2, l: 13.5, r: 13, spokes: 10 };
const CAB = { f0: -17, f1: 12, l: 11, u0: 17, u1: 40 };
/** One spoke's turn of the wheel, rolled along the ground (world pixels). */
export const WHEEL_PERIOD = (2 * Math.PI * WHEEL.r) / WHEEL.spokes;

function wheel(R: Raster, side: 1 | -1, turn: number): void {
  const c: P3 = [WHEEL.f, side * WHEEL.l, WHEEL.r];
  // Iron tyre: a band round the rim.
  for (let w = -1.4; w <= 1.4; w += 0.3) {
    const steps = Math.ceil(WHEEL.r * 2 * Math.PI * DENS);
    for (let i = 0; i < steps; i++) {
      const a = (2 * Math.PI * i) / steps;
      const nf = Math.sin(a);
      const nu = Math.cos(a);
      R.put(c[0] + nf * WHEEL.r, c[1] + w, c[2] + nu * WHEEL.r, nf, 0, nu, M.iron);
    }
  }
  // Wooden felloes: the flat ring inside the tyre, both faces.
  for (const face of [1, -1]) {
    for (let rho = 9.6; rho <= WHEEL.r - 0.3; rho += 0.3) {
      const steps = Math.ceil(rho * 2 * Math.PI * DENS);
      for (let i = 0; i < steps; i++) {
        const a = (2 * Math.PI * i) / steps;
        // Joints between the felloes, one between each pair of spokes.
        const joint = Math.abs((((a - turn) / (2 * Math.PI)) * (WHEEL.spokes / 2)) % 1) < 0.02;
        R.put(
          c[0] + Math.sin(a) * rho,
          c[1] + face * 1.2,
          c[2] + Math.cos(a) * rho,
          0,
          face,
          0,
          M.wood,
          joint ? -0.3 : 0,
        );
      }
    }
  }
  // Spokes, turned by `turn`.
  for (let k = 0; k < WHEEL.spokes; k++) {
    const a = turn + (2 * Math.PI * k) / WHEEL.spokes;
    const p0: P3 = [c[0] + Math.sin(a) * 2.6, c[1], c[2] + Math.cos(a) * 2.6];
    const p1: P3 = [c[0] + Math.sin(a) * 10, c[1], c[2] + Math.cos(a) * 10];
    R.tube(p0, p1, 0.75, 0.6, M.wood, false);
  }
  // Hub: a stout drum with a bronze cap and the linchpin.
  R.tube([c[0], c[1] - 3.2, c[2]], [c[0], c[1] + 3.2, c[2]], 2.7, 2.7, M.woodDark, false);
  R.ellipsoid([c[0], c[1] + side * 3.3, c[2]], 1.9, 0.8, 1.9, M.bronze);
  R.put(c[0], c[1] + side * 4.1, c[2], 0, side, 0, M.iron);
}

/** The cabin's sides: lacquered panels framed in dark wood, a window with a bamboo blind. */
function cabinPaint(occupied: boolean) {
  return (face: string, s: number, t: number): Mat | number | null => {
    // Corner posts and the rails top and bottom.
    const edge = s < 0.06 || s > 0.94 || t < 0.1 || t > 0.92;
    if (face === 'u+') return M.frame;
    if (edge) return M.frame;
    if (face === 'l+' || face === 'l-') {
      const win = s > 0.3 && s < 0.72 && t > 0.44 && t < 0.84;
      const sill = s > 0.27 && s < 0.75 && t > 0.4 && t < 0.88;
      if (win) {
        // Raised half way over a rider: the dark cabin and a head seen within.
        if (occupied && t < 0.68) {
          const hx = (s - 0.44) / 0.07;
          const hy = (t - 0.56) / 0.1;
          if (hx * hx + hy * hy < 1) return hy > 0.2 || Math.abs(hx) > 0.75 ? M.hair : M.skin;
          if (t < 0.49 && Math.abs(s - 0.44) < 0.13) return M.tunic;
          return M.interior;
        }
        // The blind's slats, rolled at the top when raised.
        const rows = (t - 0.44) / 0.4;
        return Math.floor(rows * 9) % 2 ? M.blind : M.blindSlat;
      }
      if (sill) return M.frame;
      // Panels: a moulding round each of two panels below.
      if (t < 0.4 && (Math.abs(s - 0.5) < 0.025 || t > 0.36)) return M.lacquerDark;
      return 0;
    }
    if (face === 'f+') {
      // The doorway at the front: an indigo curtain hung in folds, tied back on one side.
      const door = s > 0.22 && s < 0.78 && t > 0.06;
      if (door) {
        const fold = Math.floor(((s - 0.22) / 0.56) * 7) % 2;
        return t < 0.18 ? M.curtainFold : fold ? M.curtain : M.curtainFold;
      }
      return 0;
    }
    if (face === 'f-') {
      // The back: a panel with a small lattice window.
      const win = s > 0.36 && s < 0.64 && t > 0.5 && t < 0.78;
      if (win) return (Math.floor(s * 40) + Math.floor(t * 40)) % 3 === 0 ? M.frame : M.interior;
      return t < 0.4 && t > 0.36 ? M.lacquerDark : 0;
    }
    return 0;
  };
}

function coachLayer(view: View, frame: number, occupied: boolean): Layer {
  const R = new Raster(view);
  const turn = -(frame / FRAMES) * ((2 * Math.PI) / WHEEL.spokes);
  // Undercarriage: the axle and the two long beams the cabin sits on, running out into the shafts.
  R.tube([WHEEL.f, -WHEEL.l, WHEEL.r], [WHEEL.f, WHEEL.l, WHEEL.r], 1.3, 1.3, M.woodDark, false);
  for (const s of [1, -1]) {
    R.tube([-20, s * 8.5, 15], [CAB.f1 + 4, s * 8.5, 16], 1.5, 1.4, M.wood);
    R.chain(
      [
        [CAB.f1 + 4, s * 8.5, 16],
        [34, s * 8.4, 20.5],
        [52, s * 8.2, 25],
        [60, s * 7.6, 27.5],
      ],
      1.35,
      1,
      M.wood,
    );
    R.ellipsoid([60.6, s * 7.5, 27.8], 1.2, 1.2, 1.2, M.bronze);
  }
  // Floor and the footboard the coachman sits on.
  R.box(CAB.f0 - 1.5, CAB.f1 + 1.5, -CAB.l - 1.2, CAB.l + 1.2, 14.5, CAB.u0, M.woodDark);
  R.box(CAB.f1, CAB.f1 + 10, -9.5, 9.5, 14.5, 16.4, M.wood, (face, s) =>
    face === 'u+' && Math.floor(s * 6) % 2 ? -0.18 : 0,
  );
  // The cabin.
  R.box(CAB.f0, CAB.f1, -CAB.l, CAB.l, CAB.u0, CAB.u1, M.lacquer, cabinPaint(occupied));
  // Roof: an oiled-cloth vault on ribs, overhanging all round, a ridge beam with bronze ends.
  const rf0 = CAB.f0 - 4;
  const rf1 = CAB.f1 + 5;
  for (let f = rf0; f <= rf1; f += 0.3) {
    const rib = Math.abs(((f - rf0) / (rf1 - rf0)) * 6 - Math.round(((f - rf0) / (rf1 - rf0)) * 6)) < 0.06;
    const edge = f < rf0 + 0.6 || f > rf1 - 0.6;
    for (let a = -1.35; a <= 1.35; a += 0.035) {
      const l = Math.sin(a) * 14.5;
      const u = CAB.u1 + 1 + Math.cos(a) * 7 - 1.5;
      const nl = Math.sin(a) / 14.5;
      const nu = Math.cos(a) / 7;
      const nn = Math.hypot(nl, nu);
      const eave = Math.abs(a) > 1.22;
      R.put(
        f,
        l,
        u,
        edge ? Math.sign(f) * 0.6 : 0,
        nl / nn,
        nu / nn,
        rib || edge || eave ? M.roofRib : M.roof,
      );
    }
  }
  R.tube([rf0 - 0.8, 0, CAB.u1 + 6.9], [rf1 + 0.8, 0, CAB.u1 + 6.9], 1.1, 1.1, M.lacquerDark);
  for (const f of [rf0 - 1.4, rf1 + 1.4]) R.ellipsoid([f, 0, CAB.u1 + 7.4], 1.2, 1.2, 1.6, M.bronze);
  // A lantern hung at the front corner.
  R.tube([CAB.f1 + 3, -CAB.l - 1, CAB.u1 + 2], [CAB.f1 + 3, -CAB.l - 1, CAB.u1 - 2], 0.3, 0.3, M.iron, false);
  R.ellipsoid([CAB.f1 + 3, -CAB.l - 1, CAB.u1 - 4.5], 1.7, 1.7, 2.4, mat('#c8603a', 0.4, 10));
  // The coachman on the footboard: straw hat, a grey-green working tunic, reins in both hands.
  const cf = CAB.f1 + 4.5;
  R.ellipsoid([cf, 0, 21], 3.6, 4.6, 3, M.tunicDark);
  for (const s of [1, -1]) {
    R.chain(
      [
        [cf + 1, s * 2.2, 20],
        [cf + 5, s * 2.4, 19.5],
        [cf + 5.6, s * 2.3, 16.6],
      ],
      1.5,
      1.1,
      M.tunicDark,
    );
    R.ellipsoid([cf + 6.3, s * 2.3, 16.6], 1.4, 0.9, 0.7, M.leather);
  }
  R.ellipsoid([cf - 0.2, 0, 26.5], 2.8, 4, 5, M.tunic);
  R.tube([cf + 0.6, -3.9, 24], [cf + 0.6, 3.9, 24], 0.7, 0.7, M.sash, false);
  for (const s of [1, -1])
    R.chain(
      [
        [cf - 0.2, s * 3.9, 29.5],
        [cf + 2.4, s * 4.4, 25.5],
        [cf + 5.4, s * 3.4, 25.6],
      ],
      1.3,
      1,
      M.tunic,
    );
  for (const s of [1, -1]) R.ellipsoid([cf + 5.9, s * 3.3, 25.6], 0.9, 0.9, 0.9, M.skin);
  R.ellipsoid([cf + 0.4, 0, 33.2], 2.6, 2.4, 2.9, M.skin);
  R.ellipsoid([cf - 0.4, 0, 34.2], 2.2, 2.3, 2.4, M.hair);
  // The hat: a wide shallow cone of woven straw.
  for (let rho = 0; rho <= 6.8; rho += 0.3)
    for (let a = 0; a < 2 * Math.PI; a += 0.3 / Math.max(1, rho)) {
      const ring = Math.floor(rho / 1.7) % 2;
      const nu = 1;
      const nr = 0.45;
      R.put(
        cf + 0.2 + Math.cos(a) * rho,
        Math.sin(a) * rho,
        39 - rho * 0.48,
        Math.cos(a) * nr,
        Math.sin(a) * nr,
        nu,
        M.straw,
        ring ? -0.1 : 0,
      );
    }
  // The wheels outside the beams.
  wheel(R, 1, turn);
  wheel(R, -1, turn);
  return R;
}

// ───────────────────────── the horse ─────────────────────────

export type Gait = 'idle' | 'walk' | 'trot';

/** Where the horse stands (world pixels ahead of the coach's middle): body, shoulders, hips. */
const HORSE = { f: 39, fore: 50.5, hind: 28.5, wide: 3.6 };
/** Ground covered by one full cycle of each gait (world pixels). */
export const STRIDE: Record<Gait, number> = { idle: 1, walk: 36, trot: 50 };

/** A leg's foot offset (forward) and lift at phase `p` of its own cycle, `swing` of it in the air. */
function legAt(p: number, swing: number, reach: number, lift: number): [number, number] {
  const q = ((p % 1) + 1) % 1;
  if (q < swing) {
    const k = q / swing;
    return [-reach + 2 * reach * (0.5 - 0.5 * Math.cos(Math.PI * k)), Math.sin(Math.PI * k) * lift];
  }
  const k = (q - swing) / (1 - swing);
  return [reach - 2 * reach * k, 0];
}

function horseLayer(view: View, gait: Gait, frame: number): Layer {
  const R = new Raster(view);
  const ph = frame / FRAMES;
  const tau = 2 * Math.PI;
  // Phase of each leg (near fore, far fore, near hind, far hind), its swing share, reach and lift.
  let legs: [number, number][] = [
    [0, 0],
    [0, 0],
    [0, 0],
    [0, 0],
  ];
  let bob: number;
  let nod: number;
  let tail: number;
  let ear = 0;
  if (gait === 'walk') {
    // Four beats: near hind, near fore, far hind, far fore.
    const at = [0.25, 0.75, 0, 0.5];
    legs = at.map((o) => legAt(ph + o, 0.36, 4.6, 3)) as [number, number][];
    bob = 0.5 * Math.cos(2 * tau * ph);
    nod = 1.2 * Math.cos(2 * tau * ph + 0.6);
    tail = Math.sin(tau * ph) * 1.2;
  } else if (gait === 'trot') {
    // Diagonal pairs together: near fore with far hind, far fore with near hind.
    const at = [0, 0.5, 0.5, 0];
    legs = at.map((o) => legAt(ph + o, 0.46, 6.2, 5)) as [number, number][];
    bob = 1.1 * Math.cos(2 * tau * ph);
    nod = 0.6 * Math.cos(2 * tau * ph + 0.4);
    tail = Math.sin(tau * ph) * 2 + 1.5;
  } else {
    // Standing: slow breath, the tail swishing at flies, an ear turning.
    bob = 0.35 * Math.sin(tau * ph);
    nod = 0.6 * Math.sin(tau * ph + 1);
    tail = Math.sin(tau * ph * 2) * 2.2;
    ear = Math.max(0, Math.sin(tau * ph)) * 1.2;
    // One hind leg resting, the hoof tipped.
    legs[3] = [1.2, 0.8];
  }
  const B = HORSE.f;
  const bu = 26 + bob;
  // Body: barrel, chest and rump, with the shoulder and quarter muscles over them.
  R.ellipsoid([B, 0, bu], 13.5, 6.4, 7.4, M.coat);
  R.ellipsoid([B + 11.5, 0, bu + 1], 5.4, 5.8, 7.4, M.coat);
  R.ellipsoid([B - 11.5, 0, bu + 2.2], 6.4, 6.2, 7, M.coat);
  for (const s of [1, -1]) {
    R.ellipsoid([HORSE.fore - 0.5, s * 3.4, bu - 1.2], 4.8, 3.2, 7.2, M.coat);
    R.ellipsoid([HORSE.hind - 0.5, s * 3.3, bu - 0.4], 6, 3.2, 8, M.coat);
  }
  // Legs: forearm and cannon to the fetlock and hoof, feathered pale at the foot like a cart horse.
  const legPts = (fore: boolean, side: 1 | -1, [df, lift]: [number, number]): P3[] => {
    const j = fore ? HORSE.fore : HORSE.hind;
    const l = side * HORSE.wide;
    const hoof: P3 = [j + df, l, 1.4 + lift];
    if (fore) {
      // The knee folds back as the foot lifts.
      const knee: P3 = [j + df * 0.55 + lift * 0.5, l, 11 + lift * 0.55];
      const fet: P3 = [hoof[0] - lift * 0.45, l, 3.2 + lift];
      return [[j, l, bu - 4], knee, fet, hoof];
    }
    // The hock points back.
    const hock: P3 = [j + df * 0.45 - 2.6, l, 12 + lift * 0.4];
    const fet: P3 = [hoof[0] + lift * 0.2, l, 3.2 + lift];
    return [[j + 0.6, l, bu - 3], hock, fet, hoof];
  };
  const order: [boolean, 1 | -1, number][] = [
    [true, 1, 0],
    [true, -1, 1],
    [false, 1, 2],
    [false, -1, 3],
  ];
  for (const [fore, side, k] of order) {
    const [top, joint, fet, hoof] = legPts(fore, side, legs[k]!);
    const far = side < 0 ? -0.12 : 0;
    R.tube(top!, joint!, fore ? 2.6 : 3, 1.6, M.coat, true, far);
    R.tube(joint!, fet!, 1.4, 1.2, M.points, true, far);
    R.ellipsoid([fet![0], fet![1], fet![2] - 0.6], 1.9, 1.8, 1.6, M.feather);
    R.ellipsoid([hoof![0] + 0.4, hoof![1], hoof![2] - 0.2], 1.9, 1.7, 1.2, M.hoof);
  }
  // Neck rising to the head, a dark mane along its crest.
  const neck0: P3 = [B + 12.5, 0, bu + 3.5];
  const poll: P3 = [B + 19.5, 0, bu + 15 + nod];
  R.tube(neck0, poll, 5.2, 3.2, M.coat);
  R.ellipsoid([B + 16, 0, bu + 7 + nod * 0.5], 4, 4, 5.5, M.coat);
  // The head: long, down and forward from the poll, the muzzle soft and dark.
  const muzzle: P3 = [B + 26.5, 0, bu + 6.8 + nod];
  R.tube(poll, muzzle, 3.2, 2.1, M.coat);
  R.ellipsoid([B + 20.6, 0, bu + 12.6 + nod], 3.4, 2.9, 3.4, M.coatDark);
  R.ellipsoid([muzzle[0] + 0.4, 0, muzzle[2] - 0.3], 2.4, 2.3, 2.1, M.muzzle);
  // The blaze down the face.
  R.tube([B + 21.4, 0, bu + 15.4 + nod], [B + 26.3, 0, bu + 8.4 + nod], 0.9, 0.7, M.blaze, false, 0.1);
  for (const s of [1, -1]) {
    R.ellipsoid([B + 21.6, s * 2.5, bu + 13.4 + nod], 0.7, 0.5, 0.7, M.eye);
    // Ears, pricked, one turning back now and then.
    const tilt = s > 0 ? ear : 0;
    R.tube(
      [B + 19.6, s * 1.4, bu + 17 + nod],
      [B + 19.4 - tilt, s * 1.9, bu + 20.4 + nod - tilt * 0.4],
      0.9,
      0.4,
      M.coatDark,
    );
  }
  // Mane: a dark fall of hair along the crest, and the forelock.
  for (let k = 0; k <= 6; k++) {
    const t = k / 6;
    const p: P3 = [
      neck0[0] - 1.6 + (poll[0] - neck0[0]) * t,
      0.9,
      neck0[2] + 5 + (poll[2] + 1.5 - neck0[2] - 5) * t,
    ];
    R.ellipsoid(p, 1.6, 1.5, 1.9, M.points);
  }
  R.ellipsoid([B + 20.8, 0, bu + 16.2 + nod], 1.3, 1.6, 1.2, M.points);
  // Tail: a long hank of hair from the dock, swishing.
  const dock: P3 = [B - 17.5, 0, bu + 4];
  R.chain(
    [
      dock,
      [B - 20, tail * 0.4, bu - 1],
      [B - 20.8, tail * 0.8, bu - 8],
      [B - 20.2 + Math.abs(tail) * 0.2, tail, bu - 14],
    ],
    2.2,
    1.4,
    M.points,
  );
  // Harness: the padded collar, hames with bronze knobs, the saddle pad and girth, the breeching.
  R.ring([B + 13.4, 0, bu + 4.6], [0, 1, 0], [0.42, 0, 0.9], 5.3, 1.25, M.leather);
  for (const s of [1, -1]) R.ellipsoid([B + 13.6, s * 2.2, bu + 10.6], 0.9, 0.9, 1, M.bronze);
  R.ellipsoid([B + 1.5, 0, bu + 7], 4.2, 6, 1.4, M.leather);
  R.ring([B + 1.8, 0, bu - 0.4], [0, 1, 0], [0, 0, 1], 7.3, 0.6, M.leather, 0, Math.PI * 2);
  R.ring([B - 12, 0, bu + 1.5], [0, 1, 0], [0, 0, 1], 6.9, 0.55, M.leather, -Math.PI / 2, Math.PI / 2);
  // Bridle: cheek straps and noseband, the bit ring, and the reins back to the coachman's hands.
  for (const s of [1, -1]) {
    R.tube(
      [B + 20.2, s * 2.6, bu + 15.4 + nod],
      [B + 25, s * 2.2, bu + 8.6 + nod],
      0.4,
      0.4,
      M.leather,
      false,
    );
    R.ellipsoid([B + 26, s * 2.4, bu + 7.5 + nod], 0.7, 0.4, 0.7, M.bronze);
    R.chain(
      [
        [B + 26, s * 2.4, bu + 7.5 + nod],
        [B + 14, s * 4.6, bu + 9.4],
        [B - 1, s * 4.2, 27.2],
        [CAB.f1 + 10.5, s * 3.4, 25.6],
      ],
      0.35,
      0.35,
      M.leather,
    );
    // Traces from the collar back to the shafts.
    R.tube([B + 13.8, s * 5.6, bu + 1], [B - 4, s * 7.6, 22.8], 0.45, 0.45, M.leather, false);
  }
  R.ring([B + 25.2, 0, bu + 8 + nod], [0, 1, 0], [0.5, 0, 0.87], 2.6, 0.4, M.leather, 0, Math.PI);
  return R;
}

// ───────────────────────── the signpost and hitching rail ─────────────────────────

function signLayer(view: View): Layer {
  const R = new Raster(view);
  // A stone footing and a stout post, a little tiled cap, the hanging board.
  R.ellipsoid([0, 0, 1.2], 3.6, 3.6, 1.8, M.stone);
  R.tube([0, 0, 0], [0, 0, 40], 1.5, 1.4, M.woodDark);
  R.box(-3.6, 3.6, -3.6, 3.6, 40, 41.6, M.roofRib);
  R.box(-2.6, 2.6, -2.6, 2.6, 41.6, 43, M.roof);
  R.tube([0, 0, 37], [0, 7.5, 37], 0.7, 0.7, M.woodDark);
  for (const l of [1.6, 6.4]) R.tube([0, l, 37], [0, l, 34.6], 0.25, 0.25, M.iron, false);
  // The board: lacquered red-brown, its painted name stroked down it in pale ochre.
  R.box(-0.7, 0.7, 0.8, 7.2, 19, 34.6, M.board, (face, s, t) => {
    if (face !== 'l+' && face !== 'l-' && face !== 'f+' && face !== 'f-') return 0;
    if (face === 'l+' || face === 'l-') return t > 0.94 || t < 0.04 ? M.frame : 0;
    const col = Math.abs(s - 0.5) < 0.14;
    const strokes = [0.18, 0.3, 0.46, 0.6, 0.76];
    const mark =
      strokes.some((y) => Math.abs(t - y) < 0.035) ||
      (col && t > 0.12 && t < 0.86 && Math.floor(t * 12) % 3 === 0);
    if (s < 0.1 || s > 0.9 || t < 0.06 || t > 0.94) return M.frame;
    return mark && s > 0.2 && s < 0.8 ? M.paint : 0;
  });
  // The hitching rail beside it: two posts and a worn bar, a water trough at its foot.
  for (const f of [-14, -4]) R.tube([f, -6, 0], [f, -6, 15], 1.1, 1, M.wood);
  R.tube([-15.5, -6, 13.5], [-2.5, -6, 13.5], 0.9, 0.9, M.wood);
  R.box(-13.5, -4.5, -10.5, -7.6, 0, 5, M.woodDark, (face, s, t) =>
    face === 'u+'
      ? s > 0.1 && s < 0.9 && t > 0.15 && t < 0.85
        ? mat('#3c5a66', 0.9, 20)
        : M.woodDark
      : t > 0.8
        ? -0.15
        : 0,
  );
  return R;
}

// ───────────────────────── frames, put together ─────────────────────────

/** Contact shadows where one part lies over another, a dark outline, and the hard edges. */
function finish(col: Uint32Array, near: Float32Array, shadow: Uint8Array | null): HTMLCanvasElement {
  const c = canvas(CARRIAGE_W, CARRIAGE_H);
  const g = ctx2d(c);
  const img = g.createImageData(CARRIAGE_W, CARRIAGE_H);
  const o = new Uint32Array(img.data.buffer);
  for (let y = 0; y < CARRIAGE_H; y++)
    for (let x = 0; x < CARRIAGE_W; x++) {
      const i = y * CARRIAGE_W + x;
      const px = col[i]!;
      if (!(px >>> 24)) continue;
      // A part well in front just above or to the right (toward the light) shades this one.
      const n = near[i]!;
      const over =
        (y > 0 && col[i - CARRIAGE_W]! >>> 24 && near[i - CARRIAGE_W]! > n + 4) ||
        (x + 1 < CARRIAGE_W && col[i + 1]! >>> 24 && near[i + 1]! > n + 4);
      if (over) {
        const r = px & 255;
        const gg = (px >>> 8) & 255;
        const b = (px >>> 16) & 255;
        o[i] =
          (255 << 24) |
          (Math.round(b * 0.66 + 10) << 16) |
          (Math.round(gg * 0.6 + 4) << 8) |
          Math.round(r * 0.56);
      } else o[i] = px;
    }
  g.putImageData(img, 0, 0);
  const out = outline(c, '#140e0c');
  if (!shadow) return out;
  // The shadow on the ground beneath, under everything else (soft cool shade, hard-edged).
  const s = canvas(CARRIAGE_W, CARRIAGE_H);
  const sg = ctx2d(s);
  sg.fillStyle = 'rgba(18,20,38,0.34)';
  for (let y = 0; y < CARRIAGE_H; y++)
    for (let x = 0; x < CARRIAGE_W; x++)
      if (shadow[y * CARRIAGE_W + x] || shadow[y * CARRIAGE_W + x - 1] || shadow[(y - 1) * CARRIAGE_W + x])
        sg.fillRect(x, y, 1, 1);
  sg.drawImage(out, 0, 0);
  return s;
}

const layers = new Map<string, Layer>();
const layer = (key: string, make: () => Layer): Layer => {
  let l = layers.get(key);
  if (!l) layers.set(key, (l = timed(make)));
  return l;
};
const frames = new Map<string, HTMLCanvasElement>();
/** Pictures kept at most (oldest let go first): a rolling coach shows many wheel-and-gait pairs. */
const KEEP = 360;
const keep = (key: string, c: HTMLCanvasElement): HTMLCanvasElement => {
  frames.set(key, c);
  if (frames.size > KEEP) frames.delete(frames.keys().next().value!);
  return c;
};
/** The last picture made for each view, to stand in while a new one waits for room. */
const lastOf = new Map<string, HTMLCanvasElement>();
/** Shown for the frame or two a coach's first picture takes to make. */
const BLANK = canvas(1, 1);

/** What one picture of the coach shows. */
export interface CarriagePose {
  view: View;
  /** Wheel frame (0 – FRAMES−1, one spoke's turn) and the horse's gait and frame. */
  wheel: number;
  gait: Gait;
  frame: number;
  /** A rider at the window. */
  occupied: boolean;
}

/** The coach and its horse in one pose: one picture, shadow beneath, anchored at the coach's middle. */
export function carriageFrame(p: CarriagePose): HTMLCanvasElement {
  const key = `${p.view}|${p.wheel}|${p.gait}|${p.frame}|${p.occupied ? 1 : 0}`;
  const done = frames.get(key);
  if (done) return done;
  const stand = lastOf.get(p.view);
  const ck = `c|${p.view}|${p.wheel}|${p.occupied ? 1 : 0}`;
  const hk = `h|${p.view}|${p.gait}|${p.frame}`;
  // One piece per frame under the art budget: the coach, the horse and the finished picture together took 30–45 ms
  // in one frame when a coach first came into view (lane C0-P1). Meanwhile the last picture stands in, or nothing.
  if (!canDraw()) return stand ?? BLANK;
  if (!layers.has(ck)) {
    layer(ck, () => coachLayer(p.view, p.wheel, p.occupied));
    return stand ?? BLANK;
  }
  if (!layers.has(hk)) {
    layer(hk, () => horseLayer(p.view, p.gait, p.frame));
    return stand ?? BLANK;
  }
  const a = layers.get(ck)!;
  const b = layers.get(hk)!;
  const n = CARRIAGE_W * CARRIAGE_H;
  const col = new Uint32Array(n);
  const near = new Float32Array(n);
  const shadow = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    const front = b.near[i]! > a.near[i]! ? b : a;
    col[i] = front.col[i]!;
    near[i] = front.near[i]!;
    shadow[i] = a.shadow[i]! | b.shadow[i]!;
  }
  const c = timed(() => finish(col, near, shadow));
  lastOf.set(p.view, c);
  return keep(key, c);
}

/** The station's signpost and hitching rail in one view. */
export function carriageSignFrame(view: View): HTMLCanvasElement {
  const key = `sign|${view}`;
  const done = frames.get(key);
  if (done) return done;
  const l = layer(key, () => signLayer(view));
  return keep(
    key,
    timed(() => finish(l.col, l.near, l.shadow)),
  );
}

/**
 * The pose for a coach that has rolled `rolled` tiles at `speed` tiles/s, at world time `time`:
 * the wheels turned and the horse stepping by the ground covered, so neither slides; standing, the
 * horse idles on a slow cycle of its own.
 */
export function carriagePose(
  heading: number,
  rolled: number,
  speed: number,
  time: number,
  occupied: boolean,
) {
  const [view, mirror] = carriageView(heading);
  const dist = (rolled * TILE) / SCALE;
  const wheel = Math.floor(((dist / WHEEL_PERIOD) * FRAMES) % FRAMES);
  const gait: Gait = speed < 0.05 ? 'idle' : speed < CARRIAGE.walkSpeed ? 'walk' : 'trot';
  const cycle = gait === 'idle' ? time / 3.4 : dist / STRIDE[gait];
  const frame = Math.floor((((cycle % 1) + 1) % 1) * FRAMES) % FRAMES;
  return { pose: { view, wheel: ((wheel % FRAMES) + FRAMES) % FRAMES, gait, frame, occupied }, mirror };
}
